import React from 'react';
import { motion, Variants } from 'framer-motion';
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

import siteLogo from '../../../assets/images/arbventure/site_logo.webp';
import marketLogo from '../../../assets/images/cryptonary/marketlogo.webp';
import managerLogo from '../../../assets/images/arbventure/manager_logo.webp';
import authLogo from '../../../assets/images/cryptonary/authenticationlogo.webp';
import approvalLogo from '../../../assets/images/arbventure/approval_logo.webp';
import alertLogo from '../../../assets/images/cryptonary/alertlogo.webp';
import mobileLogo from '../../../assets/images/cryptonary/seamlessmobilelogo.webp';

const featureLogos = [
  siteLogo, marketLogo, managerLogo, authLogo, approvalLogo, alertLogo, mobileLogo,
];

const FeatureIcon = ({ src }: { src: string }) => (
  <div
    className="
      relative flex items-center justify-center
      h-12 w-12
      md:h-[67px] md:w-[67px]
      lg:h-[77px] lg:w-[77px]
      2xl:h-[100px] 2xl:w-[100px]
    "
  >
    {/* ROTATING BORDER */}
    <motion.div
      animate={{ rotate: 360 }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear',
      }}
      className="
        absolute inset-0 rounded-full p-[2px]
      "
      style={{
        background:
          'conic-gradient(from 0deg, #5F9937, rgba(95,153,55,0.1), #5F9937)',
      }}
    >
      {/* INNER MASK */}
      <div className="h-full w-full rounded-full bg-[#071007]" />
    </motion.div>

    {/* ICON */}
    <div className="relative z-10 flex items-center justify-center">
      <img
        src={src}
        alt=""
        className="
          object-contain
          h-6 w-6
          md:h-8 md:w-8
          lg:h-9 lg:w-9
          2xl:h-12 2xl:w-12
        "
      />
    </div>
  </div>
);

const KeyFeaturesSection = () => {
  const { arbventureKeyFeatures } = Constants;

  const leftVariant: Variants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeIn' },
    },
  };

  const rightVariant: Variants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: 'easeIn' },
    },
  };

  return (
    <section className="relative overflow-hidden pb-12 pt-12 md:pb-16 md:pt-16 lg:pb-20 2xl:pb-24 min-[2560px]:pt-32 min-[2560px]:pb-32">
      <div className="mx-auto w-full max-w-[1180px] px-5 text-center md:px-8 lg:px-12 xl:max-w-[1320px] 2xl:max-w-[1580px] 2xl:px-16 min-[2560px]:max-w-[2200px] min-[2560px]:px-24">

        {/* BADGE — continuously pulses */}
        <motion.div
          className="inline-flex items-center justify-center px-6 py-3 lg:px-7 lg:py-3.5 2xl:px-9 2xl:py-4 min-[2560px]:px-12 min-[2560px]:py-6"
          style={{ backgroundColor: colors.arbVentureAccent }}
        >
          <span className="font-gilroy-bold text-[2rem] font-semibold leading-none text-white lg:text-[2.35rem] xl:text-[2.7rem] 2xl:text-[3.25rem] min-[2056px]:text-[3.8rem] min-[2560px]:text-[4.8rem]">
            {arbventureKeyFeatures.badgeText}
          </span>
        </motion.div>

        <div className="mx-auto mt-12 flex max-w-fit flex-col gap-4 md:mt-16 md:gap-5 lg:gap-6 min-[2560px]:mt-24 min-[2560px]:gap-12">
          {arbventureKeyFeatures.items.map((item, index) => {
            const isColumnRight = index % 2 === 0;

            return (
              <div
                key={item.text}
                className={`grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 md:gap-x-4 lg:gap-x-5 min-[2560px]:gap-x-12 ${
                  isColumnRight
                    ? 'md:-translate-x-20 min-[2560px]:-translate-x-40'
                    : 'md:translate-x-20 min-[2560px]:translate-x-40'
                }`}
              >
                {/* LEFT COLUMN — text slides from left when isColumnRight is false */}
                <div className="flex justify-end pr-1 md:pr-2 min-[2560px]:pr-6">
                  {!isColumnRight && (
                    <motion.p
                      variants={leftVariant}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: false, amount: 0.5 }}
                      className="font-gilroy-bold text-[0.70rem] leading-none text-white md:whitespace-nowrap md:text-[1rem] lg:text-[1.3rem] xl:text-[1.5rem] min-[2560px]:text-[2.2rem]"
                    >
                      {item.text}
                    </motion.p>
                  )}
                </div>

                {/* CENTER — icon pulses */}
                <FeatureIcon src={featureLogos[index]} />

                {/* RIGHT COLUMN — text slides from right when isColumnRight is true */}
                <div className="flex justify-start pl-1 md:pl-2 min-[2560px]:pl-6">
                  {isColumnRight && (
                    <motion.p
                      variants={rightVariant}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: false, amount: 0.5 }}
                      className="font-gilroy-bold text-[0.70rem] leading-none text-white md:whitespace-nowrap md:text-[1rem] lg:text-[1.3rem] xl:text-[1.5rem] min-[2560px]:text-[2.2rem]"
                    >
                      {item.text}
                    </motion.p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default KeyFeaturesSection;